import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchAccounts, fetchFilterOptions } from '../services/usersService';

const ACCOUNT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Option lists for the filter menus.
 *
 * Permissions and job roles are bounded, so they load once and the menus narrow
 * them in the browser. Accounts are not — even inside the Sentinel OEM Segment
 * there are hundreds — so they are fetched per search term, which is why that menu
 * is the one the design gives a search input.
 *
 * @param {number} refreshKey bumped to re-ask for the accounts (see below)
 */
const useFilterOptions = (refreshKey = 0) => {
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [jobRoleOptions, setJobRoleOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    fetchFilterOptions()
      .then(({ permissions, jobRoles }) => {
        if (cancelled) return;
        setPermissionOptions(permissions);
        setJobRoleOptions(jobRoles);
      })
      // A failed option list leaves the menus empty rather than breaking the
      // table, which is the part of the page that matters.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // What the accounts list currently reflects, so a refresh re-asks the same
  // question rather than silently widening a menu the user had narrowed.
  const accountQuery = useRef('');

  /**
   * Accounts, re-asked whenever `refreshKey` changes.
   *
   * UserFilterOptions.accounts offers only accounts that already have a listable
   * user, so that picking one cannot empty the table. Creating a user is therefore
   * the one action on this page that can add an option: assigning the first user to
   * an account makes it eligible, and until this refetches the account the new user
   * belongs to is missing from the very menu you would filter by to find them.
   *
   * Permissions and job roles need no such refresh. Roles are global and the job
   * role list is static, so a create can only pick from what is already offered.
   */
  useEffect(() => {
    let cancelled = false;

    fetchAccounts(accountQuery.current)
      .then((accounts) => {
        if (!cancelled) setAccountOptions(accounts);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // SearchInput's searchOn="change" fires per keystroke — its documented
  // debounceTimeout is never read by the component — so the debounce lives here,
  // where the request is.
  const searchTimer = useRef(null);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  const searchAccounts = useCallback((query) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    accountQuery.current = query;

    searchTimer.current = setTimeout(async () => {
      try {
        setAccountOptions(await fetchAccounts(query));
      } catch {
        setAccountOptions([]);
      }
    }, ACCOUNT_SEARCH_DEBOUNCE_MS);
  }, []);

  return { accountOptions, jobRoleOptions, permissionOptions, searchAccounts };
};

export default useFilterOptions;
